import React, { useCallback, useEffect, useState } from "react";
import AppLayout from "@/components/AppLayout";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Landmark, Save } from "lucide-react";
import { useAuth } from "@/lib/auth";

export default function AdminPaymentSettings() {
  const { user } = useAuth();
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const canEdit = user?.role === "super_admin" || user?.role === "founder";

  const load = useCallback(async () => {
    try {
      const r = await api.get("/admin/payment-settings");
      setS(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cannot load payment settings");
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const upd = (k) => (e) => setS((v) => ({ ...v, [k]: e?.target ? e.target.value : e }));

  const save = async () => {
    if (!canEdit) return;
    setSaving(true);
    try {
      const payload = ["legal_business_name","gstin","billing_address","bank_account_name","bank_name",
                       "account_number","ifsc","branch","upi_id","upi_qr_doc_id","payment_instructions",
                       "payment_support_phone","payment_support_email"]
                     .reduce((a, k) => (s?.[k] !== undefined ? { ...a, [k]: s[k] } : a), {});
      await api.patch("/admin/payment-settings", payload);
      toast.success("Payment settings updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  if (!s) return <AppLayout><div className="p-6 text-sm text-[var(--miro-text-muted)]">Loading…</div></AppLayout>;

  return (
    <AppLayout>
      <div className="p-6 max-w-3xl" data-testid="admin-payment-settings">
        <div className="miro-overline">Approval Centre · Payment</div>
        <h1 className="font-display font-black text-3xl text-[var(--miro-navy)] tracking-tight">
          Official payment settings
        </h1>
        <p className="text-sm text-[var(--miro-text-secondary)] mt-2 max-w-lg leading-relaxed">
          These are the ONLY bank and UPI details shown to partner applicants on their
          payment page. Only Super Admin can modify. Every change is written to the audit log.
        </p>

        <div className="mt-6 bg-white border border-[var(--miro-border)] rounded-xl p-6 space-y-5">
          <Row title="Business identity">
            <FieldInput label="Legal business name" value={s.legal_business_name} onChange={upd("legal_business_name")} disabled={!canEdit} testid="ps-legal-name"/>
            <FieldInput label="GSTIN" value={s.gstin} onChange={upd("gstin")} disabled={!canEdit} testid="ps-gstin"/>
            <div className="sm:col-span-2">
              <Label>Billing address</Label>
              <Textarea data-testid="ps-billing-address" rows={2} value={s.billing_address || ""} onChange={upd("billing_address")} disabled={!canEdit} className="mt-1"/>
            </div>
          </Row>

          <Row title="UPI">
            <FieldInput label="Official UPI ID" value={s.upi_id} onChange={upd("upi_id")} disabled={!canEdit} testid="ps-upi-id"/>
          </Row>

          <Row title="Bank">
            <FieldInput label="Account holder name" value={s.bank_account_name} onChange={upd("bank_account_name")} disabled={!canEdit} testid="ps-bank-name-holder"/>
            <FieldInput label="Bank name" value={s.bank_name} onChange={upd("bank_name")} disabled={!canEdit} testid="ps-bank-name"/>
            <FieldInput label="Account number" value={s.account_number} onChange={upd("account_number")} disabled={!canEdit} testid="ps-account-number"/>
            <FieldInput label="IFSC" value={s.ifsc} onChange={upd("ifsc")} disabled={!canEdit} testid="ps-ifsc"/>
            <FieldInput label="Branch" value={s.branch} onChange={upd("branch")} disabled={!canEdit} testid="ps-branch"/>
          </Row>

          <Row title="Instructions & support">
            <div className="sm:col-span-2">
              <Label>Payment instructions shown to applicants</Label>
              <Textarea data-testid="ps-instructions" rows={2} value={s.payment_instructions || ""} onChange={upd("payment_instructions")} disabled={!canEdit} className="mt-1"/>
            </div>
            <FieldInput label="Support phone" value={s.payment_support_phone} onChange={upd("payment_support_phone")} disabled={!canEdit} testid="ps-support-phone"/>
            <FieldInput label="Support email" value={s.payment_support_email} onChange={upd("payment_support_email")} disabled={!canEdit} testid="ps-support-email"/>
          </Row>

          <div className="flex items-center justify-between pt-4 border-t border-[var(--miro-border)]">
            <div className="flex items-center gap-1.5 text-xs text-[var(--miro-text-muted)]">
              <Landmark size={12}/> Never accept payments to any account other than the one configured here.
            </div>
            {canEdit && (
              <Button data-testid="ps-save-btn" onClick={save} disabled={saving}
                      className="h-10 gap-1.5" style={{ background: "var(--miro-navy)" }}>
                <Save size={13}/> {saving ? "Saving…" : "Save settings"}
              </Button>
            )}
          </div>
        </div>
      </div>
    </AppLayout>
  );
}

function Row({ title, children }) {
  return (
    <div>
      <div className="miro-overline mb-2">{title}</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">{children}</div>
    </div>
  );
}

function FieldInput({ label, value, onChange, disabled, testid }) {
  return (
    <div>
      <Label>{label}</Label>
      <Input data-testid={testid} value={value || ""} onChange={onChange} disabled={disabled}
             className="mt-1 h-11 bg-white"/>
    </div>
  );
}
